import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { authAPI } from '../../services/api';
import { formatApiErrorDetail } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';

export const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const t = searchParams.get('token');
    if (t) setToken(t);
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (!token) {
      toast.error('Invalid or missing reset token');
      return;
    }
    setLoading(true);
    try {
      await authAPI.resetPassword(token, newPassword);
      toast.success('Password reset successful! Please sign in.');
      navigate('/login');
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Reset failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-background">
      <div className="w-full max-w-md space-y-8">
        <div>
          <h2 className="text-3xl font-semibold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="reset-heading">
            Reset Password
          </h2>
          <p className="mt-2 text-muted-foreground">Enter your new password below</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="reset-form">
          <div className="space-y-2">
            <Label>Reset Token</Label>
            <Input
              value={token}
              onChange={(e) => setToken(e.target.value)}
              required
              placeholder="Paste your reset token"
              data-testid="reset-token-input"
              className="h-11 font-mono text-xs"
            />
          </div>

          <div className="space-y-2">
            <Label>New Password</Label>
            <Input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              placeholder="••••••••"
              data-testid="reset-new-password-input"
              className="h-11"
            />
            <p className="text-xs text-muted-foreground">Must be at least 8 characters</p>
          </div>

          <div className="space-y-2">
            <Label>Confirm New Password</Label>
            <Input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              placeholder="••••••••"
              data-testid="reset-confirm-password-input"
              className="h-11"
            />
          </div>

          <Button type="submit" disabled={loading} className="w-full h-11" data-testid="reset-submit-button">
            {loading ? 'Resetting...' : 'Reset Password'}
          </Button>
        </form>

        <p className="text-center text-sm text-muted-foreground">
          <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium">
            Back to Sign in
          </Link>
        </p>
      </div>
    </div>
  );
};
